import { useEffect, useRef, useState } from "react";

/**
 * Returns animated progress and controls that simulate Zeno's Dichotomy Paradox,
 * where progress approaches but never immediately reaches 100.
 *
 * @returns {[number, () => void, () => void, (value: number) => void]} Tuple:
 *   - progress: Current progress value (0–100).
 *   - start: Start the progress animation (resets progress to 0 and animates toward 100).
 *   - stop: Stop and reset the progress animation.
 *   - set: Set the progress to a specific value (if 100, stops animation).
 */
export default function useZenosDichotomyParadox(): [
  number,
  () => void,
  () => void,
  (value: number) => void,
] {
  const [progress, setProgress] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const interval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRunning) {
      interval.current = setInterval(() => {
        setProgress((prev) => {
          // Zeno's Dichotomy paradox: move slowly toward halfway point
          // Instead of jumping halfway immediately, move 1% of the remaining distance
          const remaining = 100 - prev;
          const stepSize = remaining * 0.01; // 1% of remaining distance
          return prev + stepSize;
        });
      }, 100); // Update every 100ms for smoother animation
    } else if (interval.current) {
      clearInterval(interval.current);
    }
    // Cleanup interval on unmount
    return () => {
      interval.current && clearInterval(interval.current);
    };
  }, [isRunning]);

  const start = () => {
    setProgress(0);
    if (!isRunning) setIsRunning(true);
  };

  const stop = () => {
    setProgress(0);
    if (isRunning) setIsRunning(false);
  };

  const set = (value: number) => {
    setProgress(value);
    if (value === 100) setIsRunning(false);
  };

  return [progress, start, stop, set];
}
